import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getJson, postJson } from '../global/func';
import Icon from './Icon';

// How the buttons are labelled, and what each one warns about. The names come
// from DeviceControl::ALLOWED on the server; anything not in that list is
// refused there, so this list is presentation rather than policy.
const COMMANDS = [
    { name: 'reload-page', label: 'Reload the display', icon: 'refresh' },
    { name: 'reboot', label: 'Reboot the box', icon: 'power',
      confirm: 'Reboot the Prayer Box? The screen will be blank for about a minute.' },
    { name: 'enter-ap-mode', label: 'Start Wi-Fi setup', icon: 'box',
      confirm: 'This disconnects the box from your Wi-Fi and starts its setup network. '
             + 'You will need to reconnect it before prayer times return. Continue?' },
    // Support only. What it returns is raw systemd journal: the first thing
    // worth having when someone rings up, and noise to the person whose living
    // room the box is in.
    { name: 'fetch-logs', label: 'Fetch logs', icon: 'download', adminOnly: true },
];

// A box checks in every fifteen minutes when idle, so silence shorter than that
// is normal and should not be dressed up as a fault.
const OFFLINE_AFTER_MINUTES = 45;

// Ages come from the server, already measured. Subtracting timestamps here was
// wrong in a way that was invisible: the host runs PHP in UTC and MySQL in
// Eastern time, and a phone adds a third zone, while "2026-09-03 14:39:09"
// declares none of them. A box that had reported seven minutes earlier was
// called silent.
const minutesFromSeconds = (seconds) => {
    if (seconds === null || seconds === undefined || seconds === '') return null;
    const n = Number(seconds);
    if (!Number.isFinite(n)) return null;
    // Clocks that disagree can put a reading slightly in the future. That is not
    // a reason to say "never".
    return Math.max(0, Math.round(n / 60));
};

const describeAge = (minutes) => {
    if (minutes === null) return 'never';
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours} h ago`;
    return `${Math.round(hours / 24)} days ago`;
};

const describeUptime = (seconds) => {
    if (!seconds && seconds !== 0) return '—';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const DeviceHealth = ({ User, Device }) => {

    const [history, setHistory] = useState([]);
    // Health comes from the server rather than from the device list, which the
    // app writes at sign-in and keeps in local storage. A box that started
    // reporting after that would have gone on looking silent until the customer
    // signed out and back in -- which is exactly what it did.
    const [health, setHealth] = useState(null);
    // Reported by the server, which decides it from the database. Held only so
    // the panel knows what to draw -- it grants nothing, and the server checks
    // again on every request regardless of what is drawn here.
    const [isAdmin, setIsAdmin] = useState(false);
    // Owner or member, straight from the reply this panel already reads.
    //
    // A member is shown how the box is doing and nothing about commanding it:
    // status can be looked at, and none of it can be acted on. The buttons below
    // come from a constant in this file rather than from the server's list, so
    // hiding them is this component's job -- the server refuses the command
    // either way, but a button that always fails is a worse way to find out.
    const [role, setRole] = useState(null);
    const [busy, setBusy] = useState('');
    // Administrator-only, and fetched rather than typed: offering the versions
    // that exist beats a text box where a typo becomes a refusal the box
    // discovers minutes later.
    const [releases, setReleases] = useState([]);
    const [chosenVersion, setChosenVersion] = useState('');
    const [applyNow, setApplyNow] = useState(false);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');

    const deviceKey = Device?.device_key;

    const loadHistory = useCallback(async () => {
        if (!deviceKey) return;
        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/command/');
        // getJson, not getData: this endpoint answers {commands, available,
        // health} with no `data` member, and getData returns that member. It was
        // therefore handing back undefined every time, which is indistinguishable
        // from an empty result -- the panel showed "never" while the box was
        // checking in every fifteen minutes.
        const result = await getJson(url, { DEVICE_KEY: deviceKey });
        const body = result.ok ? result.data : null;
        setHistory(Array.isArray(body?.commands) ? body.commands : []);
        if (body?.health) setHealth(body.health);
        setIsAdmin(body?.is_admin === true);
        if (body?.role) setRole(body.role);
    }, [deviceKey]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;

        (async () => {
            const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/releases/');
            const result = await getJson(url, { USER_ID: User.id, AUTH_KEY: User.auth_key });
            if (cancelled) return;

            const list = Array.isArray(result.data?.releases) ? result.data.releases : [];
            setReleases(list);
            // Newest first from the server, so the default is the newest.
            setChosenVersion((current) => current || list[0]?.version || '');
        })();

        return () => { cancelled = true; };
    }, [isAdmin, User.id, User.auth_key]);

    // Thirty seconds is a quiet background cadence: it keeps the panel honest
    // for someone who leaves it open, without asking anything much of the
    // server.
    //
    // It is far too slow for the half minute after pressing a button. The box
    // is now woken the moment a command is queued and typically answers within
    // a couple of seconds, and a panel that took another twenty-eight to admit
    // it would make a fast box look like a broken one. So the first stretch
    // after queueing is watched closely and then it settles down.
    const [watching, setWatching] = useState(false);

    useEffect(() => {
        const timer = setInterval(loadHistory, watching ? 3000 : 30000);
        return () => clearInterval(timer);
    }, [loadHistory, watching]);

    // Long enough for the box to be woken, check in, do the work and report
    // back. A command that has not finished by then is one that is not going to
    // finish quickly, and watching harder will not help it along.
    useEffect(() => {
        if (!watching) return undefined;
        const timer = setTimeout(() => setWatching(false), 45000);
        return () => clearTimeout(timer);
    }, [watching]);

    const send = async (command, extra = {}) => {
        const chosen = COMMANDS.find((c) => c.name === command);
        if (chosen?.confirm && !window.confirm(chosen.confirm)) {
            return;
        }

        setBusy(command);
        setError('');
        setNotice('');

        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/command/');
        const result = await postJson(url, {
            command,
            ...extra,
            DEVICE_KEY: deviceKey,
            USER_ID: User.id,
            AUTH_KEY: User.auth_key,
        });

        setBusy('');

        if (result.ok) {
            setNotice(result.data?.message || 'Queued.');
            loadHistory();
            setWatching(true);
            return;
        }

        setError(result.status === 0
            ? 'Could not reach the server. Check your connection and try again.'
            : (result.data?.error || 'That command could not be queued.'));
    };

    // last_checkin only exists once the remote control migration has run. Until
    // then the panel says so plainly rather than reporting every box as offline,
    // which would be a lie told confidently.
    const reported = health || Device;
    const supported = reported && ('checkin_age_seconds' in reported || 'last_checkin' in reported);
    // A box that has never checked in reports a null age, which is not the same
    // as an age of zero.
    const since = (reported && reported.last_checkin)
        ? minutesFromSeconds(reported.checkin_age_seconds)
        : null;
    const online = since !== null && since < OFFLINE_AFTER_MINUTES;

    // A box that refuses to display still checks in, and until now it checked in
    // reporting the same mode as a healthy one -- so the single state most worth
    // seeing was the one that looked green. The agent now reports "locked", and
    // it outranks Online here: the box is reachable, which is exactly why saying
    // "Online" would be misleading rather than merely incomplete.
    const locked = online && reported.last_mode === 'locked';

    if (!supported) {
        return (
            <p className="muted">
                This Prayer Box has not reported its status yet. Health reporting arrives
                with the next server update.
            </p>
        );
    }

    return (
        <>
            <div className="field" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <span className="field__label">Status</span>
                    <div style={{ fontSize: '.95rem' }}>{describeAge(since)}</div>
                </div>
                <span className={'pill ' + (locked ? 'pill--warn' : online ? 'pill--ok' : '')}>
                    {locked ? 'Locked' : online ? 'Online' : 'Not reporting'}
                </span>
            </div>

            <div className="stats">
                <div><span className="field__label">Uptime</span>{describeUptime(reported.uptime_seconds)}</div>
                <div><span className="field__label">Temperature</span>{reported.temperature_c ? `${reported.temperature_c} °C` : '—'}</div>
                <div><span className="field__label">Software</span>{reported.agent_version || '—'}</div>
            </div>

            {locked &&
                <div className="notice notice--warn" role="status">
                    <strong>This box is online but not displaying.</strong>
                    It has stopped showing prayer times and is asking its owner to contact
                    support. It is reachable and still accepting commands.
                </div>}

            {!online &&
                <div className="notice notice--warn" role="status">
                    This box has not checked in recently. That is normal if it is unplugged;
                    if it is switched on, its internet connection is the first thing to look
                    at. Commands queued now will run when it reconnects.
                </div>}

            {notice && <div className="notice notice--info" role="status">{notice}</div>}
            {error && <div className="notice notice--warn" role="alert">{error}</div>}

            {role !== 'member' && <div className="btn-row">
                {COMMANDS.filter((c) => !c.adminOnly || isAdmin).map((c) => (
                    <button key={c.name}
                        type="button"
                        className={'btn btn--sm' + (c.confirm ? ' btn--warn' : '')}
                        disabled={busy !== ''}
                        onClick={() => send(c.name)}>
                        {busy === c.name ? <span className="spinner" /> : <Icon name={c.icon} size={15} />}
                        {c.label}
                    </button>
                ))}
            </div>}

            {/* Administrator-only. An owner can restart their box and reload its
                display; deciding which build it runs is not theirs, and the
                server refuses it from any other account regardless of what is
                drawn here. */}
            {isAdmin &&
                <div className="panel">
                    <div className="panel__head">
                        <Icon name="download" size={15} />
                        <strong>Software</strong>
                        <span className="menu__meta">on this box: {reported?.agent_version || 'unknown'}</span>
                    </div>

                    {releases.length === 0 ? (
                        <p className="sheet__sub" style={{ margin: '.5rem 0 0' }}>No releases are published yet.</p>
                    ) : (
                        <>
                            <div className="field" style={{ marginTop: '.6rem' }}>
                                <label className="field__label" htmlFor="release">Version</label>
                                <select id="release" value={chosenVersion} disabled={busy !== ''}
                                        onChange={(e) => setChosenVersion(e.currentTarget.value)}>
                                    {releases.map((r) => (
                                        <option key={r.version} value={r.version}>
                                            {r.version}{r.version === reported?.agent_version ? ' (installed)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="switch">
                                <span className="switch__text" id="apply-now-label">
                                    Apply now
                                    <small>
                                        {applyNow
                                            ? 'Restarts as soon as the box collects this.'
                                            : 'Held until 2am–5am, so nobody is watching when it restarts.'}
                                    </small>
                                </span>
                                <button type="button" role="switch" aria-checked={applyNow}
                                        aria-labelledby="apply-now-label" className="knob"
                                        disabled={busy !== ''}
                                        onClick={() => setApplyNow(!applyNow)} />
                            </div>

                            <button type="button"
                                className="btn btn--primary btn--block"
                                disabled={busy !== '' || !chosenVersion}
                                onClick={() => {
                                    const when = applyNow
                                        ? 'The box will restart within a few minutes and its screen will be blank for about a minute.'
                                        : 'It will install itself tonight between 2am and 5am and restart then.';
                                    if (!window.confirm(`Install ${chosenVersion} on this Prayer Box?\n\n${when}\n\nIf it does not come back up healthy it puts itself back automatically.`)) {
                                        return;
                                    }
                                    send('update', { version: chosenVersion, apply_now: applyNow });
                                }}>
                                {busy === 'update'
                                    ? <><span className="spinner" />Queueing…</>
                                    : <><Icon name="download" size={15} />Install {chosenVersion}</>}
                            </button>
                        </>
                    )}
                </div>}

            {history.length > 0 &&
                <div style={{ marginTop: '1.2rem' }}>
                    <div className="field__label" style={{ marginBottom: '.4rem' }}>Recent commands</div>
                    <div style={{ maxHeight: '16rem', overflowY: 'auto' }}>
                        {history.map((h) => (
                            <div key={h.id} className="cmd">
                                <span className="cmd__name">{h.command}</span>
                                <span className={'pill' + (
                                    h.status === 'done' ? ' pill--ok'
                                        : h.status === 'failed' ? ' pill--warn' : '')}>
                                    {h.status}
                                </span>
                                <span className="cmd__age">{describeAge(minutesFromSeconds(h.age_seconds))}</span>
                                {isAdmin && h.result &&
                                    <details className="cmd__out">
                                        <summary>output</summary>
                                        <pre>{h.result}</pre>
                                    </details>}
                            </div>
                        ))}
                    </div>
                </div>}
        </>
    );
};

DeviceHealth.propTypes = {
    User: PropTypes.object.isRequired,
    Device: PropTypes.object,
};

export default DeviceHealth;
